/********************************************************************
 * ADOBE CONFIDENTIAL
 * __________________
 *
 *  Copyright 2024 Adobe
 *  All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 *******************************************************************/

import { useCallback, StateUpdater, Dispatch } from 'preact/hooks';
import { FunctionComponent, JSX, VNode } from 'preact';
import { GiftOptionModal } from './Elements/GiftOptionModal';
import { GiftLoader } from './Elements/GiftLoader';
import { CheckboxGroup } from './Elements/CheckboxGroup';
import { FormFields } from './Elements/FormFields';
import { ReadOnlyFormView } from './Elements/ReadOnlyFormView';
import { classes } from '@adobe-commerce/elsie/lib';
import {
  Accordion,
  AccordionSection,
  Card,
  Icon,
  ImageNodeRenderProps,
  ProgressSpinner,
} from '@adobe-commerce/elsie/components';
import {
  ChevronDown,
  ChevronUp,
  CheckWithCircle,
  Gift,
} from '@adobe-commerce/elsie/icons';
import {
  GiftWrappingConfigProps,
  GiftOptionsViewProps,
  GiftFormDataType,
  GiftOptionsReadOnlyViewProps,
  ProductGiftOptionsConfig,
} from '@/cart/types';
import { useText } from '@adobe-commerce/elsie/i18n';
import { CartModel, Item } from '@/cart/data/models';
import { decodeHTMLEntities } from '@/cart/lib/decodeHTMLEntities';
import '@/cart/components/GiftOptions/GiftOptions.css';

export interface GiftOptionsProps {
  readOnlyFormOrderView: GiftOptionsReadOnlyViewProps;
  cartData: CartModel | null;
  errorsField: Record<string, string>;
  isGiftMessageVisible: boolean;
  fieldsDisabled: boolean;
  loading: boolean;
  showModal: boolean;
  isEditable: boolean;
  isGiftOptionsApplied: boolean;
  updateLoading: boolean;
  areGiftOptionsVisible: Record<string, boolean>;
  view: GiftOptionsViewProps;
  giftOptions: GiftFormDataType;
  imageSwatchImageNode?: VNode | ((props: ImageNodeRenderProps) => JSX.Element);
  item: Item | ProductGiftOptionsConfig;
  giftWrappingConfig: GiftWrappingConfigProps[] | [];
  updateGiftOptions: (
    name: string,
    value?: string | boolean | number,
    extraGiftOptions?: Record<string, string | boolean | number>
  ) => void;
  setShowModal: Dispatch<StateUpdater<boolean>>;
  handleFormMouseLeave: () => void;
  onInputChange: (event: Event) => void;
  onBlur: (event: Event) => void;
}

export const GiftOptions: FunctionComponent<GiftOptionsProps> = ({
  item,
  view,
  loading,
  giftOptions,
  showModal,
  isEditable,
  errorsField,
  updateLoading,
  cartData,
  fieldsDisabled,
  isGiftOptionsApplied,
  giftWrappingConfig,
  readOnlyFormOrderView,
  isGiftMessageVisible,
  areGiftOptionsVisible,
  imageSwatchImageNode,
  onBlur,
  setShowModal,
  updateGiftOptions,
  onInputChange,
  handleFormMouseLeave,
}) => {
  const dictionary = useText({
    accordionHeading: `Cart.GiftOptions.${view}.accordionHeading`,
    accordionAriaLabel: 'Cart.GiftOptions.accordionAriaLabel',
    giftOptionsApplied: 'Cart.GiftOptions.giftOptionsApplied',
  });

  // Each cart item renders its own "Gift options" accordion trigger, so the
  // accessible name must include the product name to distinguish them.
  const productName =
    item && 'name' in item ? decodeHTMLEntities(item.name) : undefined;
  const accordionAriaLabelTitle = productName
    ? dictionary.accordionAriaLabel
        .replace('{title}', dictionary.accordionHeading)
        .replace('{product}', productName)
    : dictionary.accordionHeading;

  const renderAccordion = useCallback(
    (defaultOpen: boolean) => {
      return (
        <Accordion
          data-testid="gift-options-product"
          iconClose={ChevronUp}
          iconOpen={ChevronDown}
          actionIconPosition="right"
        >
          <AccordionSection
            title={
              <div className="cart-gift-options-view__icon--success">
                <span>{dictionary.accordionHeading}</span>
                {isGiftOptionsApplied ? (
                  <Icon
                    source={CheckWithCircle}
                    size="16"
                    role="img"
                    aria-label={dictionary.giftOptionsApplied}
                  />
                ) : null}
              </div>
            }
            ariaLabelTitle={accordionAriaLabelTitle}
            showIconLeft={true}
            iconLeft={Gift}
            defaultOpen={isGiftOptionsApplied || defaultOpen}
            renderContentWhenClosed={false}
          >
            <>
              <CheckboxGroup
                className={'cart-gift-options-view__top'}
                view={view}
                item={item}
                giftOptions={giftOptions}
                disabled={fieldsDisabled}
                onInputChange={onInputChange}
                cartData={cartData}
                giftWrappingConfig={giftWrappingConfig}
                setShowModal={setShowModal}
                areGiftOptionsVisible={areGiftOptionsVisible}
              />
              <form
                className="cart-gift-options-view__footer"
                onMouseLeave={handleFormMouseLeave}
              >
                <FormFields
                  view={view}
                  giftOptions={giftOptions}
                  disabled={fieldsDisabled}
                  errorMessage={errorsField}
                  onInputChange={onInputChange}
                  onBlur={onBlur}
                  isGiftMessageVisible={isGiftMessageVisible}
                />
              </form>
            </>
          </AccordionSection>
        </Accordion>
      );
    },
    [
      view,
      item,
      cartData,
      dictionary,
      accordionAriaLabelTitle,
      errorsField,
      giftOptions,
      fieldsDisabled,
      giftWrappingConfig,
      areGiftOptionsVisible,
      isGiftOptionsApplied,
      isGiftMessageVisible,
      onBlur,
      setShowModal,
      onInputChange,
      handleFormMouseLeave,
    ]
  );

  if (!areGiftOptionsVisible.isGiftOptionsVisible && !isGiftMessageVisible) {
    return null;
  }

  return (
    <div
      id="cart-gift-options-view"
      className={classes([
        'cart-gift-options-view',
        `cart-gift-options-view--${view}`,
        ['cart-gift-options-view--loading', updateLoading],
      ])}
    >
      {updateLoading ? (
        <ProgressSpinner className="cart-gift-options-view__spinner" />
      ) : null}
      {!loading ? (
        <>
          {!isEditable ? (
            <div className={'cart-gift-options-view--readonly'}>
              <ReadOnlyFormView
                view={view}
                giftOptions={giftOptions}
                giftWrappingConfig={giftWrappingConfig}
                readOnlyFormOrderView={readOnlyFormOrderView}
              />
            </div>
          ) : null}
          {showModal ? (
            <GiftOptionModal
              view={view}
              productName={item && 'name' in item ? item?.name : ''}
              showModal={showModal}
              giftWrappingConfig={giftWrappingConfig}
              setShowModal={() => setShowModal(false)}
              updateGiftOptions={updateGiftOptions}
              imageSwatchImageNode={imageSwatchImageNode}
            />
          ) : null}
          {view === 'product' && isEditable ? renderAccordion(false) : null}
          {view === 'order' && isEditable ? (
            <Card variant={'secondary'}>{renderAccordion(true)}</Card>
          ) : null}
        </>
      ) : (
        <GiftLoader />
      )}
    </div>
  );
};
